import { useTranslation } from 'react-i18next';
import { Flex, Field, Spinner, Text, VStack } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import {
  Button as ButtonComponent,
  InputField,
  SectionCard,
  SelectInput,
  SubHeader,
  ToggleButton,
  Tooltip,
} from '@themes/aegix';
import { HiPlus } from 'react-icons/hi2';
import { FaRegEdit } from 'react-icons/fa';
import {
  useOrganizations,
  useSchoolPreselectedProviderOrgs,
} from '../../hooks/queries';
import { SiteFormInitialValues } from '../../types/SitesFormInitialValue';
import {
  BlueprintsStaging,
  type StagedBlueprint,
} from '../sites/drawers/BlueprintsStaging';
import { PerSiteDrawer } from './PerSiteDrawer';
import { t } from 'i18next';
import { useState } from 'react';

interface SitesFormProps {
  staged?: StagedBlueprint[];
  onStagedChange?: (next: StagedBlueprint[]) => void;
}

const SitesForm: React.FC<SitesFormProps> = ({
  staged,
  onStagedChange,
}) => {
  const { t } = useTranslation();
  const { formState: { errors, touchedFields }, setValue, watch } =
    useFormContext<SiteFormInitialValues>();
  const values = watch();
  const [isPerSiteDrawerOpen, setIsPerSiteDrawerOpen] = useState(false);
  const siteId = values.id ? String(values.id) : '';
  const { data: preselectedData, isLoading: isLoadingPreselected } =
    useSchoolPreselectedProviderOrgs(siteId);

  const { data: orgOptions = [], isError: orgError } = useOrganizations();

  // The `organization` form field carries the numeric District id
  // (as a string), NOT the org public_id. On submit it is translated to the
  // backend's writable `district` FK (see useSitesFloorPlansData.handleSubmit).
  // Binding to public_id silently dropped the org because SchoolSerializer has
  // no writable `organization` field — only the numeric `district`.
  const organizationOptions = orgOptions.map((o) => ({
    label: o.label,
    value: String(o.id),
  }));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValue(name as keyof SiteFormInitialValues, value);
  };
  return (
    <Flex flexDirection={'column'} gap={4}>
      <Field.Root
        invalid={!!touchedFields.organization && !!errors.organization}
      >
        <SelectInput
          value={values?.organization}
          name="organization"
          label={t('common.organization', 'Organization')}
          option={organizationOptions}
          onChange={(val: string) =>
            setValue('organization', val, { shouldTouch: true })
          }
          error={
            touchedFields.organization
              ? errors.organization?.message
              : undefined
          }
          data-e2e="select-organization"
        />
      </Field.Root>
      {orgError && (
        <Text fontSize="xs" color="text.danger">
          {t('sites.failedLoadOrgs', 'Failed to load organizations. Please try refreshing.')}
        </Text>
      )}

      <Field.Root invalid={!!errors.name}>
        <Field.Label>{t('common.name', 'Name')}</Field.Label>
        <InputField
          name="name"
          placeholder={t('placeholders.site_name', 'Site name')}
          value={values.name}
          onChange={handleChange}
          data-e2e="input-site-name"
        />
        {errors.name && <Field.ErrorText data-e2e="site-name-error">{errors.name.message ? t(errors.name.message) : null}</Field.ErrorText>}
      </Field.Root>

      <SubHeader label={t('resources.col.location', 'Location')} />

      <Field.Root invalid={!!errors.address}>
        <Field.Label>{t('common.address', 'Address')}</Field.Label>
        <InputField
          name="address"
          placeholder={t('common.address', 'Address')}
          value={values.address ?? ''}
          onChange={handleChange}
          data-e2e="input-site-address"
        />
        {errors.address && (
          <Field.ErrorText data-e2e="site-address-error">{errors.address.message ? t(errors.address.message) : null}</Field.ErrorText>
        )}
      </Field.Root>

      <Field.Root>
        <Field.Label>{t('common.address_2', 'Address 2')}</Field.Label>
        <InputField
          name="address_2"
          placeholder={t('common.address_2', 'Address 2')}
          value={values.address_2 ?? ''}
          onChange={handleChange}
          data-e2e="input-site-address-2"
        />
      </Field.Root>

      <Field.Root>
        <Field.Label>{t('common.city', 'City')}</Field.Label>
        <InputField
          name="city"
          placeholder={t('common.city', 'City')}
          value={values.city ?? ''}
          onChange={handleChange}
          data-e2e="input-site-city"
        />
      </Field.Root>

      <Field.Root>
        <Field.Label>{t('common.state', 'State')}</Field.Label>
        <InputField
          name="state"
          placeholder={t('common.state', 'State')}
          value={values.state ?? ''}
          onChange={handleChange}
          data-e2e="input-site-state"
        />
      </Field.Root>

      <Field.Root>
        <Field.Label>{t('common.zip', 'Zip')}</Field.Label>
        <InputField
          name="zip"
          placeholder={t('common.zip', 'Zip')}
          value={values.zip ?? ''}
          onChange={handleChange}
          data-e2e="input-site-zip"
        />
      </Field.Root>

      <Field.Root>
        <Field.Label>{t('common.country', 'Country')}</Field.Label>
        <InputField
          name="country"
          placeholder={t('common.country', 'Country')}
          value={values.country ?? ''}
          onChange={handleChange}
          data-e2e="input-site-country"
        />
      </Field.Root>

      <Field.Root>
        <Field.Label>{t('sites.phoneNumber', 'Phone Number')}</Field.Label>
        <InputField
          name="phone_number"
          placeholder="+1 (555) 123-4567"
          value={values.phone_number ?? ''}
          onChange={handleChange}
          data-e2e="input-site-phone"
        />
        <Field.HelperText fontSize={'10px'} color="text.tertiary">
          {t('sites.mainPhoneHelp', 'Main contact number for the site')}
        </Field.HelperText>
      </Field.Root>

      <Field.Root invalid={!!errors.latitude}>
        <Field.Label>{t('sites.latitude', 'Latitude')}</Field.Label>
        <InputField
          name="latitude"
          placeholder={t('sites.latitude', 'Latitude')}
          value={values.latitude ?? ''}
          onChange={handleChange}
          data-e2e="input-site-latitude"
        />
        {errors.latitude && (
          <Field.ErrorText data-e2e="site-latitude-error">{errors.latitude.message ? t(errors.latitude.message) : null}</Field.ErrorText>
        )}
      </Field.Root>

      <Field.Root invalid={!!errors.longitude}>
        <Field.Label>{t('sites.longitude', 'Longitude')}</Field.Label>
        <InputField
          name="longitude"
          placeholder={t('sites.longitude', 'Longitude')}
          value={values.longitude ?? ''}
          onChange={handleChange}
          data-e2e="input-site-longitude"
        />
        {errors.longitude && (
          <Field.ErrorText data-e2e="site-longitude-error">{errors.longitude.message ? t(errors.longitude.message) : null}</Field.ErrorText>
        )}
      </Field.Root>

      <Field.Root invalid={!!errors.elevation}>
        <Field.Label>{t('sites.elevation', 'Elevation')}</Field.Label>
        <InputField
          name="elevation"
          placeholder={t('sites.elevation', 'Elevation')}
          value={values.elevation ?? ''}
          onChange={handleChange}
          data-e2e="input-site-elevation"
        />
        {errors.elevation && (
          <Field.ErrorText data-e2e="site-elevation-error">{errors.elevation.message ? t(errors.elevation.message) : null}</Field.ErrorText>
        )}
        <Field.HelperText fontSize={'10px'} color="text.tertiary">
          {t('sites.elevationHelp', 'Site elevation in meters above sea level')}
        </Field.HelperText>
      </Field.Root>

      <SubHeader label={t('sites.licensing', 'Licensing')} />

      <ToggleButton
        e2eTitle="licensed"
        name="licensed"
        title={t('sites.licensedSite', 'Licensed Site')}
        checked={values.licensed}
        onChange={() => {
          setValue('licensed', !values.licensed);
        }}
      />
      <Text
        fontSize="xs"
        color="text.tertiary"
        mt={-2}
        data-e2e="site-licensed-helper"
      >
        {values.licensed
          ? t('sites.licensedHelperTrue', 'Uploaded blueprints will be processed by the map provider to generate dynamic maps.')
          : t('sites.licensedHelperFalse', 'Uploaded blueprints will be used as static images on AIM.')}
      </Text>

      {onStagedChange && (
        <>
          <SubHeader label={t('sites.blueprints', 'Blueprints')} />
          <BlueprintsStaging
            staged={staged ?? []}
            onChange={onStagedChange}
          />
        </>
      )}

      <SubHeader label={t('events.toggle.alerts', 'Alerts')} />

      <ToggleButton
        e2eTitle="drill_enabled"
        name="drill_enabled"
        title={t('sites.showDrillButton', 'Show Drill Button')}
        checked={values.drill_enabled}
        onChange={() => {
          setValue('drill_enabled', !values.drill_enabled);
        }}
      />
      <ToggleButton
        e2eTitle="show_safe_unsafe"
        name="show_safe_unsafe"
        title={t('sites.promptSafetyStatus', 'Prompt Safety Status')}
        checked={values.show_safe_unsafe}
        onChange={() => {
          setValue('show_safe_unsafe', !values.show_safe_unsafe);
        }}
      />

      {/* ----------------------------------------------------------------- */}
      {/* Per Site                                                          */}
      {/* ----------------------------------------------------------------- */}
      <SectionCard
        title="Per Site"
        action={(() => {
          const hasProviderOrgs =
            (preselectedData?.results && preselectedData.results.length > 0) ||
            (values.preselected_provider_ids ?? []).length > 0;
          const label = hasProviderOrgs
            ? 'Edit provider organizations'
            : 'Add provider organizations';
          return (
            <Tooltip content={label}>
              <ButtonComponent
                visual="secondary"
                size="sm"
                type="button"
                aria-label={label}
                onClick={() => setIsPerSiteDrawerOpen(true)}
                data-e2e="add-per-site-provider-button"
              >
                {hasProviderOrgs ? (
                  <FaRegEdit size={14} />
                ) : (
                  <HiPlus size={14} />
                )}
              </ButtonComponent>
            </Tooltip>
          );
        })()}
      >
        {isLoadingPreselected ? (
          <Flex align="center" gap={2} py={1}>
            <Spinner size="xs" color="button.primary" />
            <Text fontSize="xs" color="text.secondary">
              Loading...
            </Text>
          </Flex>
        ) : siteId &&
          preselectedData?.results &&
          preselectedData.results.length > 0 ? (
          <VStack gap={2} align="stretch">
            {preselectedData.results.map((item) => (
              <Flex
                key={item.id}
                justify="space-between"
                align="center"
                py={1.5}
                px={3}
                bg="surface.secondary"
                border="1px solid"
                borderColor="border.secondary"
                borderRadius="sm"
                data-e2e={`preselected-provider-item-${item.id}`}
              >
                <Text fontSize="sm" fontWeight={500} color="text.primary">
                  {item.provider_org_name}
                </Text>
              </Flex>
            ))}
          </VStack>
        ) : (values.preselected_provider_ids ?? []).length > 0 ? (
          <VStack gap={2} align="stretch">
            {orgOptions
              .filter((o) =>
                (values.preselected_provider_ids ?? []).includes(String(o.id)),
              )
              .map((item) => (
                <Flex
                  key={item.id}
                  justify="space-between"
                  align="center"
                  py={1.5}
                  px={3}
                  bg="surface.secondary"
                  border="1px solid"
                  borderColor="border.secondary"
                  borderRadius="sm"
                  data-e2e={`preselected-provider-item-${item.id}`}
                >
                  <Text fontSize="sm" fontWeight={500} color="text.primary">
                    {item.label}
                  </Text>
                </Flex>
              ))}
          </VStack>
        ) : (
          <Text fontSize="xs" color="text.secondary">
            No pre-selected provider organizations configured.
          </Text>
        )}
      </SectionCard>

      <PerSiteDrawer
        isOpen={isPerSiteDrawerOpen}
        onClose={() => setIsPerSiteDrawerOpen(false)}
        schoolId={siteId}
        action={siteId ? 'edit' : 'add'}
        initialProviderOrgIds={
          siteId
            ? (preselectedData?.provider_org_ids ??
              values.preselected_provider_ids)
            : values.preselected_provider_ids
        }
        onSaveTemp={(selectedIds) => {
          setValue('preselected_provider_ids', selectedIds, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }}
      />
    </Flex>
  );
};

export default SitesForm;
